/**
 * Messaging contracts shared by API, realtime gateway and clients (REALTIME_PROTOCOL.md, ADR-002).
 * Phase 2 bodies are plaintext on the server; `ciphertext` replaces `body` when E2EE lands (Phase 5).
 */
import { z } from 'zod';
import { MESSAGE_BODY_HARD_LIMIT, PROTOCOL_VERSION } from './constants.js';
import type { ErrorCode } from './errors.js';

/** Megolm room-event content produced by the client crypto library; opaque to the server (ADR-014). */
export const megolmContentSchema = z
  .object({
    algorithm: z.literal('m.megolm.v1.aes-sha2'),
    ciphertext: z.string().min(1).max(64 * 1024),
    session_id: z.string().min(1).max(128),
    sender_key: z.string().max(128).optional(),
    device_id: z.string().max(128).optional(),
  })
  .strict();
export type MegolmContent = z.infer<typeof megolmContentSchema>;

const sendMessageFields = z.object({
  clientMsgId: z.uuid(),
  /** Text, or the caption of an attachment (may then be empty). */
  body: z.string().max(MESSAGE_BODY_HARD_LIMIT).default(''),
  attachmentId: z.uuid().optional(),
  replyToId: z.uuid().optional(),
  /** A URL from the text whose server-fetched preview should be attached (ignored if none is cached). */
  linkPreviewUrl: z.url({ protocol: /^https?$/ }).max(2048).optional(),
  /** End-to-end encrypted content; the body must then be empty. */
  encrypted: megolmContentSchema.optional(),
  clientSentAt: z.iso.datetime().optional(),
});
const notEmpty = (m: { body: string; attachmentId?: string | undefined; encrypted?: MegolmContent | undefined }) =>
  m.encrypted ? m.body.length === 0 : m.body.trim().length > 0 || Boolean(m.attachmentId);
export const sendMessageSchema = sendMessageFields.refine(notEmpty, { message: 'empty', path: ['body'] });
export type SendMessageInput = z.infer<typeof sendMessageSchema>;

export const editMessageSchema = z.object({
  body: z.string().max(MESSAGE_BODY_HARD_LIMIT).refine((s) => s.trim().length > 0, 'empty'),
});
export const deleteMessageQuerySchema = z.object({ scope: z.enum(['everyone', 'me']).default('me') });

export const createGroupSchema = z.object({
  title: z.string().trim().min(1).max(80),
  description: z.string().trim().max(500).optional(),
  memberIds: z.array(z.uuid()).max(255).default([]),
});
export const updateGroupSchema = z
  .object({
    title: z.string().trim().min(1).max(80),
    description: z.string().trim().max(500).nullable(),
    onlyAdminsCanSend: z.boolean(),
  })
  .partial()
  .strict();
export const addMembersSchema = z.object({ userIds: z.array(z.uuid()).min(1).max(100) });
export const memberRoleSchema = z.object({ role: z.enum(['admin', 'member']) });
/** Hours a new invite link stays valid; null = until revoked. */
export const INVITE_LINK_EXPIRY_HOURS = [24, 168, 720] as const;
export const createInviteLinkSchema = z.object({
  requireApproval: z.boolean().default(false),
  expiresInHours: z
    .number()
    .int()
    .refine((h) => (INVITE_LINK_EXPIRY_HOURS as readonly number[]).includes(h), 'unsupported')
    .nullable()
    .default(null),
});
/** Invite tokens travel in request bodies (never URLs), so they stay out of access logs. */
export const groupLinkTokenSchema = z.object({ token: z.string().regex(/^[A-Za-z0-9_-]{32,64}$/) });
export const joinDecisionSchema = z.object({ approve: z.boolean() });

/** Pinned messages per conversation. */
export const MAX_PINNED_MESSAGES = 3;
/** Conversations one forward may target. */
export const MAX_FORWARD_TARGETS = 5;
/** One emoji (including ZWJ sequences, skin tones, flags and keycaps); no text. */
const EMOJI = /^(?:\p{Extended_Pictographic}|\p{Emoji_Modifier}|\p{Regional_Indicator}|\u200d|\ufe0f|\u20e3|[#*0-9])+$/u;
export const reactionSchema = z.object({
  emoji: z
    .string()
    .min(1)
    .max(16)
    .refine((e) => EMOJI.test(e) && /\p{Extended_Pictographic}|\p{Regional_Indicator}|\u20e3/u.test(e), 'not an emoji')
    .refine((e) => [...new Intl.Segmenter('en', { granularity: 'grapheme' }).segment(e)].length === 1, 'one emoji'),
});
export const forwardSchema = z.object({ conversationIds: z.array(z.uuid()).min(1).max(MAX_FORWARD_TARGETS) });

export const linkPreviewRequestSchema = z.object({ url: z.url({ protocol: /^https?$/ }).max(2048) });

export const receiptSchema = z.object({ seq: z.number().int().nonnegative() });
export const usernameRefSchema = z.object({ username: z.string().min(1).max(64) });
export const userRefSchema = z.object({ userId: z.uuid() });
/** Catch-up of edits, deletions and reactions after a reconnect (server time from GET /sync). */
export const changesQuerySchema = z.object({ since: z.iso.datetime({ offset: true }) });

export const historyQuerySchema = z.object({
  beforeSeq: z.coerce.number().int().positive().optional(),
  afterSeq: z.coerce.number().int().nonnegative().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export interface AttachmentDto {
  id: string;
  kind: 'image' | 'file' | 'voice' | 'video';
  mime: string;
  size: number;
  name: string;
  width: number | null;
  height: number | null;
  durationMs: number | null;
  hasThumbnail: boolean;
}

export interface UserSummaryDto {
  id: string;
  username: string;
  displayName: string;
}

export interface ContactDto extends UserSummaryDto {
  addedAt: string;
  /** True when the caller has blocked this user. */
  blocked: boolean;
}

export interface MessageDto {
  id: string;
  conversationId: string;
  seq: number;
  senderId: string;
  clientMsgId: string;
  contentType: 'text' | 'image' | 'file' | 'voice' | 'video' | 'encrypted';
  body: string | null;
  /** Megolm ciphertext of an end-to-end encrypted message (body is then null); null once deleted. */
  encrypted: MegolmContent | null;
  /** Present for image/file/voice messages (null once deleted). */
  attachment: AttachmentDto | null;
  replyToId: string | null;
  /** A forwarded copy (the original sender is not revealed). */
  forwarded: boolean;
  /** Grouped by emoji; empty once deleted. */
  reactions: ReactionDto[];
  /** Server-fetched preview of a link in the text. */
  linkPreview: LinkPreviewDto | null;
  createdAt: string;
  editedAt: string | null;
  /** Deleted for everyone: body is null and the content is gone from the server. */
  deleted: boolean;
}

export interface LinkPreviewDto {
  id: string;
  url: string;
  title: string | null;
  description: string | null;
  siteName: string | null;
  hasImage: boolean;
}

export type DeliveryState = 'sent' | 'delivered' | 'read';

export interface MessageInfoDto {
  members: (UserSummaryDto & { status: DeliveryState })[];
  /** True when read states are withheld (the viewer does not share read receipts). */
  readsHidden: boolean;
}

export interface StarredMessageDto {
  message: MessageDto;
  starredAt: string;
}

export interface ReactionDto {
  emoji: string;
  userIds: string[];
}

export interface PinnedMessageDto {
  messageId: string;
  seq: number;
  senderId: string;
  contentType: MessageDto['contentType'];
  /** First characters of the text (null for attachments without caption). */
  preview: string | null;
  pinnedAt: string;
}

export interface PresenceDto {
  userId: string;
  online: boolean;
  /** Only when offline and visible to the viewer. */
  lastSeenAt: string | null;
}

export type GroupRole = 'owner' | 'admin' | 'member';

export interface GroupInfoDto {
  title: string;
  description: string | null;
  memberCount: number;
  myRole: GroupRole;
  onlyAdminsCanSend: boolean;
  /** Pending join requests (0 unless the viewer is an owner or admin). */
  pendingRequests: number;
  /** Community (space) it belongs to, if any. */
  spaceId: string | null;
  /** The community's announcements channel. */
  isAnnouncements: boolean;
  /** Channel listed in the public directory. */
  isPublic: boolean;
  /** Community group that community members may join without an invite. */
  communityOpen: boolean;
}

// ---------- communities and channels (ADR-017) ----------

export const createChannelSchema = z.object({
  title: z.string().trim().min(1).max(80),
  description: z.string().trim().max(500).optional(),
  public: z.boolean().default(false),
});
export const channelDiscoverQuerySchema = z.object({ q: z.string().trim().max(80).optional() });
export interface ChannelSummaryDto {
  id: string;
  title: string;
  description: string | null;
  subscriberCount: number;
  subscribed: boolean;
}

export const createCommunityGroupSchema = z.object({
  title: z.string().trim().min(1).max(80),
  description: z.string().trim().max(500).optional(),
  open: z.boolean().default(true),
});
export const spaceInviteLinkSchema = z.object({ expiresInHours: z.number().int().refine((h) => [24, 168, 720].includes(h), 'unsupported').nullable().default(null) });
export const spaceLinkTokenSchema = z.object({ token: z.string().regex(/^[A-Za-z0-9_-]{32,64}$/) });

export interface CommunityGroupDto {
  id: string;
  title: string;
  description: string | null;
  memberCount: number;
  open: boolean;
  joined: boolean;
}
export interface CommunityDto {
  spaceId: string;
  name: string;
  type: 'community' | 'family';
  myRole: 'owner' | 'admin' | 'member' | 'guest';
  memberCount: number;
  announcementsId: string;
  groups: CommunityGroupDto[];
}
export interface SpaceInvitePreviewDto {
  name: string;
  type: string;
  memberCount: number;
  /** Set when the viewer is already a member. */
  spaceId: string | null;
}

export interface GroupInviteLinkDto {
  createdAt: string;
  requireApproval: boolean;
  expiresAt: string | null;
  useCount: number;
}

export interface GroupInvitePreviewDto {
  title: string;
  description: string | null;
  memberCount: number;
  requireApproval: boolean;
  /** Set when the viewer is already a member. */
  conversationId: string | null;
  requested: boolean;
}

export interface GroupJoinResultDto {
  status: 'joined' | 'requested';
  conversationId: string | null;
}

export interface GroupJoinRequestDto extends UserSummaryDto {
  requestedAt: string;
  /** The person was removed from this group by an admin before. */
  previouslyRemoved: boolean;
}

export interface GroupMemberDto extends UserSummaryDto {
  role: GroupRole;
}

export interface ConversationDto {
  id: string;
  type: 'direct' | 'group' | 'channel';
  /** The other person in a direct conversation; null for groups. */
  peer: UserSummaryDto | null;
  /** Group details; null for direct conversations. */
  group: GroupInfoDto | null;
  /** Newest first, at most MAX_PINNED_MESSAGES. */
  pinned: PinnedMessageDto[];
  /** End-to-end encryption is on (irreversible): only encrypted messages are accepted. */
  e2ee: boolean;
  lastSeq: number;
  lastMessage: MessageDto | null;
  unreadCount: number;
  myLastReadSeq: number;
  peerLastDeliveredSeq: number;
  peerLastReadSeq: number;
  /** Whether the caller can currently send here (blocks, privacy, verification). */
  canSend: boolean;
  /** Notifications for this conversation are muted until this time (null = not muted). */
  mutedUntil: string | null;
}

// ---------- realtime envelopes ----------

export interface ServerEnvelope<P = unknown> {
  version: typeof PROTOCOL_VERSION;
  event: string;
  event_id: string;
  conversation_id?: string;
  seq?: number;
  timestamp: string;
  payload: P;
}

export type ServerEvent =
  | ServerEnvelope<{ heartbeatIntervalMs: number; serverTime: string; limits: { maxFrameBytes: number } }> & { event: 'hello' }
  | ServerEnvelope<MessageDto> & { event: 'message.created' }
  | ServerEnvelope<MessageDto> & { event: 'message.updated' }
  | ServerEnvelope<{ userId: string; deliveredSeq: number; readSeq: number }> & { event: 'receipt.updated' }
  | ServerEnvelope<{ userId: string; typing: boolean }> & { event: 'typing.updated' }
  | ServerEnvelope<PresenceDto> & { event: 'presence.updated' }
  | ServerEnvelope<{ conversationId: string }> & { event: 'conversation.updated' }
  /** Calendar events or tasks in a scope changed; clients refetch (ADR-018). */
  | ServerEnvelope<{ scope: { type: 'personal' | 'space' | 'group'; id: string | null } }> & { event: 'planner.updated' }
  /** Classes or homework changed; clients refetch (ADR-019). */
  | ServerEnvelope<{ classId: string | null }> & { event: 'learn.updated' }
  | ServerEnvelope<{ reason: string }> & { event: 'session.revoked' }
  /** New to-device packets or device-list changes are waiting: the client calls /e2ee/sync. */
  | ServerEnvelope<Record<string, never>> & { event: 'e2ee.sync' }
  | ServerEnvelope<{ callId: string; conversationId: string; caller: UserSummaryDto; media: CallMedia; sdp: string }> & { event: 'call.incoming' }
  | ServerEnvelope<{ callId: string; sdp: string }> & { event: 'call.answered' }
  | ServerEnvelope<{ callId: string; candidate: { candidate: string; sdpMid?: string | null; sdpMLineIndex?: number | null } }> & { event: 'call.ice' }
  | ServerEnvelope<{ callId: string; reason: CallEndReason }> & { event: 'call.ended' }
  | ServerEnvelope<{ reconnectAfterMs: number }> & { event: 'server.draining' }
  | ServerEnvelope<{ reqId: string; result: unknown }> & { event: 'ack' }
  | ServerEnvelope<{ reqId?: string; code: ErrorCode; message?: string }> & { event: 'error' }
  | ServerEnvelope<Record<string, never>> & { event: 'pong' };

// ---------- templates and AI writing assistant (ADR-016) ----------

export const TEMPLATE_CATEGORIES = ['general', 'education', 'work', 'social'] as const;
export const templateInputSchema = z.object({
  title: z.string().trim().min(1).max(80),
  body: z.string().trim().min(1).max(2000),
  category: z.enum(TEMPLATE_CATEGORIES).default('general'),
});
export interface MessageTemplateDto {
  id: string;
  category: (typeof TEMPLATE_CATEGORIES)[number];
  title: string;
  body: string;
  /** The user's own template (editable); otherwise a built-in one. */
  own: boolean;
}

export const AI_ACTIONS = ['fix', 'shorten', 'formal', 'friendly', 'translate_en', 'translate_tr'] as const;
export type AiAction = (typeof AI_ACTIONS)[number];
/** Longest draft the assistant accepts. */
export const AI_MAX_INPUT_CHARS = 2000;
/** Bumped whenever the disclosure shown before consent changes: everyone is asked again. */
export const AI_CONSENT_VERSION = 'ai-2026-10-25';
export const aiRewriteSchema = z.object({ action: z.enum(AI_ACTIONS), text: z.string().trim().min(1).max(AI_MAX_INPUT_CHARS) });
export const aiConsentSchema = z.object({ consent: z.boolean() });
export interface AiStatusDto {
  /** Turned on and a provider is configured on the server. */
  available: boolean;
  /** Adults with an active account only. */
  allowedForAccount: boolean;
  consented: boolean;
  consentVersion: string;
  provider: string | null;
  dailyLimit: number;
  remainingToday: number;
}
export interface AiRewriteResultDto {
  text: string;
  /** Personal data items that were hidden from the provider. */
  redacted: number;
  /** Hidden items the model dropped from its answer (the user should check the text). */
  missingPlaceholders: number;
  remainingToday: number;
}

// ---------- calls (ADR-015) ----------

/** SDP offers/answers are a few KB; the cap keeps them well inside one WebSocket frame. */
export const MAX_SDP_LENGTH = 32 * 1024;
const sdpSchema = z.string().min(1).max(MAX_SDP_LENGTH);
const iceSchema = z.object({
  candidate: z.string().max(2048),
  sdpMid: z.string().max(64).nullable().optional(),
  sdpMLineIndex: z.number().int().min(0).max(64).nullable().optional(),
});
export type CallMedia = 'audio' | 'video';
export type CallEndReason = 'hangup' | 'rejected' | 'missed' | 'busy' | 'cancelled' | 'failed' | 'disconnected' | 'answered_elsewhere';

export interface CallDto {
  id: string;
  conversationId: string;
  peer: UserSummaryDto;
  direction: 'outgoing' | 'incoming';
  media: CallMedia;
  state: 'ringing' | 'active' | 'ended';
  endReason: CallEndReason | null;
  createdAt: string;
  answeredAt: string | null;
  endedAt: string | null;
}

export interface IceServersDto {
  iceServers: { urls: string[]; username?: string; credential?: string }[];
  /** Media must go through the relay (hides each side's IP address from the other). */
  relayOnly: boolean;
  ttlSeconds: number;
}

export const clientCommandSchema = z.discriminatedUnion('cmd', [
  z.object({ version: z.literal(PROTOCOL_VERSION), cmd: z.literal('ping'), req_id: z.string().max(64).optional() }),
  z.object({
    version: z.literal(PROTOCOL_VERSION),
    cmd: z.literal('message.send'),
    req_id: z.string().max(64),
    payload: sendMessageFields.extend({ conversationId: z.uuid() }).refine(notEmpty, { message: 'empty', path: ['body'] }),
  }),
  z.object({
    version: z.literal(PROTOCOL_VERSION),
    cmd: z.literal('receipt.read'),
    req_id: z.string().max(64).optional(),
    payload: z.object({ conversationId: z.uuid(), seq: z.number().int().nonnegative() }),
  }),
  z.object({
    version: z.literal(PROTOCOL_VERSION),
    cmd: z.literal('typing'),
    payload: z.object({ conversationId: z.uuid(), typing: z.boolean() }),
  }),
  z.object({
    version: z.literal(PROTOCOL_VERSION),
    cmd: z.literal('call.invite'),
    req_id: z.string().max(64).optional(),
    payload: z.object({ callId: z.uuid(), conversationId: z.uuid(), media: z.enum(['audio', 'video']), sdp: sdpSchema }),
  }),
  z.object({
    version: z.literal(PROTOCOL_VERSION),
    cmd: z.literal('call.answer'),
    req_id: z.string().max(64).optional(),
    payload: z.object({ callId: z.uuid(), sdp: sdpSchema }),
  }),
  z.object({
    version: z.literal(PROTOCOL_VERSION),
    cmd: z.literal('call.ice'),
    payload: z.object({ callId: z.uuid(), candidate: iceSchema }),
  }),
  z.object({
    version: z.literal(PROTOCOL_VERSION),
    cmd: z.literal('call.reject'),
    req_id: z.string().max(64).optional(),
    payload: z.object({ callId: z.uuid() }),
  }),
  z.object({
    version: z.literal(PROTOCOL_VERSION),
    cmd: z.literal('call.hangup'),
    req_id: z.string().max(64).optional(),
    payload: z.object({ callId: z.uuid(), reason: z.enum(['hangup', 'failed']).default('hangup') }),
  }),
]);
export type ClientCommand = z.infer<typeof clientCommandSchema>;

export interface MessageChangesDto {
  /** Messages whose content changed after `since` (edited, deleted, reactions), oldest change first. */
  messages: MessageDto[];
  /** Messages this person removed for themselves (on another device) after `since`. */
  hiddenIds: string[];
  /** More changes than one page: ask again with since = nextSince. */
  hasMore: boolean;
  nextSince: string;
}
